import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import {
  RepaymentCommitment,
  RetailODDetails,
  RetailOdLedgerInfoDto,
  TermLoanDetails,
  TermLoanLedgerInfoDto,
  UserDetails,
} from "../../dto";
import { EnterpriseDetails } from "../../dto/enterprise.dto";
import { LoanStatusEnum } from "../../models";

import { useState } from "react";
import ConfirmDialog, {
  ConfirmDialogType,
} from "../../components/confirm-dialog";
import { splitKeyWithSpace } from "../../utils";
import BorrowerDetails from "./loan-details/borrowerDetails";
import CurrentLoanAccountStatus from "./loan-details/currentLoanStatus";
import DisbursalDetails from "./loan-details/disbursalDetails";
import LoanSummary from "./loan-details/loanSummary";
import WrittenOffDetails from "./loan-details/writtenOffDetails";
import LoanRemarks from "./loan-remarks";
import LoanDisbursal from "./loanDisbursal";
import LoanRepaymentCommitment from "./loanRepaymentCommitment";
import LoanRepaymentSchedule from "./loanRepaymentSchedule";
import TermLoanRepayment from "./termLoanRepayment";
import { WriteOffCollectionAmount, WriteOffLoanFields } from "./types";

interface LoanDetailsSectionProps {
  loanId: string;
  userDetails: UserDetails | EnterpriseDetails | null;
  termLoanLedgerInfo: TermLoanLedgerInfoDto | null;
  retailOdLedgerInfo: RetailOdLedgerInfoDto | null;
  loanDetails: TermLoanDetails | RetailODDetails | null;
  writeOffCollectionAmount: WriteOffCollectionAmount | null;
  onWriteOffBtnClick: (
    type: ConfirmDialogType,
    fieldData: WriteOffLoanFields,
  ) => void;
  unpaidRepaymentCommitmentAmount: number | null;
  repaymentCommitments: RepaymentCommitment[] | null;
  onCloseCreditLineBtnClick: () => void;
  errorMessage: string;
}

export default function LoanDetailsSection({
  errorMessage,
  loanId,
  loanDetails,
  retailOdLedgerInfo,
  termLoanLedgerInfo,
  userDetails,
  onWriteOffBtnClick,
  writeOffCollectionAmount,
  unpaidRepaymentCommitmentAmount,
  repaymentCommitments,
  onCloseCreditLineBtnClick,
}: LoanDetailsSectionProps) {
  const { path } = useRouteMatch();
  const [isWriteOffModal, setIsWriteOffModal] = useState<{
    active: boolean;
    errorMessage: string;
  }>({ active: false, errorMessage: "" });

  if (!loanDetails) return null;

  const handleWriteOffSubmit = async (
    type: ConfirmDialogType,
    fieldData: { [x: string]: string },
  ) => {
    onWriteOffBtnClick(type, {
      writeOffDate: fieldData.writeOffDate,
      reason: fieldData.writeOffReason,
    });
  };

  return (
    <>
      <div className="dashboard-container">
        <LoanSummary
          errorMessage={errorMessage}
          loanDetails={loanDetails}
          termLoanLedgerInfo={termLoanLedgerInfo}
          retailOdLedgerInfo={retailOdLedgerInfo}
          onWriteOffBtnClick={() => {
            setIsWriteOffModal({ active: true, errorMessage: "" });
          }}
          onCloseCreditLineBtnClick={onCloseCreditLineBtnClick}
        />
      </div>
      <div className="dashboard-container">
        {/* TODO: Removing it for now It's not required for RBI Audit */}
        {/* <div className="d-flex align-items-center justify-content-end">
          <Link
            to={`${RoutePaths.INDIVIDUAL_USERS}/${userDetails?._id}/documents`}
          >
            View KYC
          </Link>
        </div> */}
        <BorrowerDetails userDetails={userDetails} loanDetails={loanDetails} />
      </div>
      {loanDetails.loanType !== "RETAIL_OD" && (
        <div className="dashboard-container">
          <DisbursalDetails loanDetails={loanDetails} />
        </div>
      )}
      <div className="dashboard-container">
        <CurrentLoanAccountStatus
          loanDetails={loanDetails}
          termLoanLedgerInfo={termLoanLedgerInfo}
          retailOdLedgerInfo={retailOdLedgerInfo}
          unpaidRepaymentCommitmentAmount={unpaidRepaymentCommitmentAmount}
        />
      </div>
      {loanDetails.loanType === "RETAIL_OD" && (
        <div className="dashboard-container">
          <LoanRemarks loanId={loanId} loanType={"RETAIL_OD"} />
        </div>
      )}
      {loanDetails?.writeOffMetadata && (
        <div className="dashboard-container">
          <WrittenOffDetails
            data={loanDetails.writeOffMetadata}
            writeOffCollectionAmount={writeOffCollectionAmount}
          />
        </div>
      )}
      {loanDetails.loanType === "TERM_LOAN" && (
        <>
          <div className="dashboard-container">
            <LoanRepaymentSchedule
              loanId={loanId}
              disbursed={
                loanDetails.status !== LoanStatusEnum.DISBURSAL_PENDING
              }
            />
          </div>
          <div className="dashboard-container">
            <TermLoanRepayment loanId={loanId} />
          </div>
          <div className="dashboard-container">
            <LoanRepaymentCommitment
              repaymentCommitments={repaymentCommitments}
            />
          </div>
          <div className="dashboard-container">
            <LoanRemarks loanId={loanId} loanType={"TERM_LOAN"} />
          </div>
          <Switch>
            <Route path={`${path}/disbursal`}>
              <div className="dashboard-container">
                <LoanDisbursal
                  loanDetails={loanDetails as TermLoanDetails}
                  userDetails={userDetails}
                />
              </div>
            </Route>
            {loanDetails.status === LoanStatusEnum.DISBURSAL_PENDING && (
              <Redirect to={`${path}/disbursal`} />
            )}
          </Switch>
        </>
      )}
      <ConfirmDialog
        active={isWriteOffModal?.active}
        title="Write off loan"
        submitButtonText="Write off"
        cancelButtonText="Cancel"
        type="success"
        message={`Are you sure you want to write off the loan? Loan ID: ${loanId}`}
        afterSuccessMessage={`${splitKeyWithSpace(
          loanDetails.loanType,
        )} with loan ID ${loanId} has been written off`}
        customErrorMessage={"Please fill all the fields"}
        onCancel={() => setIsWriteOffModal({ active: false, errorMessage: "" })}
        onSubmit={handleWriteOffSubmit}
        inputFields={[
          {
            name: "writeOffDate",
            label: "Write off date",
            type: "date",
            required: true,
            placeholder: "Write off date",
          },
          {
            name: "writeOffReason",
            label: "Write off reason",
            type: "text",
            required: true,
            placeholder: "Write off reason",
          },
        ]}
      />
    </>
  );
}
